/**
 * Outstanding worker detects, keyed by request id.
 *
 * ── Why this exists ───────────────────────────────────────────────────────
 *
 * `PoseDetectionManager.detectPose` used to attach one `message` listener per
 * call to a worker that broadcasts to ALL listeners, and each listener resolved
 * on the first `DETECT_SUCCESS` it saw. With two detects in flight — which
 * happens on any device whose detect cycle exceeds `CountdownManager`'s 250 ms
 * tick, measured at 2.9–4.6 Hz on the BUG-0008 handsets — tick N's response
 * resolved BOTH promises with N's poses, and N+1's real response was dropped.
 * `RotationManager` then classified a pose one tick stale against a fresh
 * frame, which surfaces as `under-rotation`, `rotation-too-quick` and
 * `did-not-rotate-in-place`.
 *
 * Matching responses to requests by id is the fix `HERE_BE_DRAGONS.md` names.
 *
 * ── The hazard the fix introduces, and why the timeout is not optional ────
 *
 * Correct matching means a response that never arrives no longer settles
 * anything. The old design could not hang — a stray response always resolved
 * *something* — but this one can, and `useDetectionEffects` awaits each
 * `processFrame` before requesting the next animation frame, so a single lost
 * worker message would wedge the alignment loop for the rest of the session.
 * Every request therefore carries its own timer, and settling clears it.
 *
 * Generic over the payload so the registry has no opinion about poses, which
 * is what lets it be tested without a `Worker` (Jest 27 + jsdom has none).
 */

interface Entry<T> {
  resolve: (value: T) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

export class PendingDetects<T> {
  private nextId = 0;
  private pending = new Map<number, Entry<T>>();

  /** Outstanding requests. Read by tests and by the manager's backlog guard. */
  get size(): number {
    return this.pending.size;
  }

  /**
   * Register a request and hand back its id plus the promise its response will
   * settle. The id must be sent to the worker and echoed back verbatim —
   * ordering of arrivals is never a valid substitute.
   */
  create(timeoutMs: number): { requestId: number; promise: Promise<T> } {
    const requestId = ++this.nextId;

    // Definite-assignment assertions: the executor runs synchronously inside
    // the Promise constructor, so both are assigned before `create` returns,
    // but TypeScript cannot prove that.
    let resolve!: (value: T) => void;
    let reject!: (error: Error) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });

    const timer = setTimeout(() => {
      const entry = this.pending.get(requestId);
      if (!entry) return;
      this.pending.delete(requestId);
      const error = new Error(
        "Pose detect " + requestId + " timed out after " + timeoutMs + "ms"
      );
      // Named so a caller can tell a LOST message from a detector that
      // answered with an error — the live "detector broken" signal treats
      // one timeout as decisive and needs three errors (spec §4.6).
      error.name = DETECT_TIMEOUT_ERROR;
      entry.reject(error);
    }, timeoutMs);

    this.pending.set(requestId, { resolve, reject, timer });
    return { requestId, promise };
  }

  /** Settle one request. Unknown or already-settled ids are ignored, not thrown. */
  resolve(requestId: number, value: T): void {
    const entry = this.take(requestId);
    if (entry) entry.resolve(value);
  }

  /** Fail one request. Unknown or already-settled ids are ignored, not thrown. */
  reject(requestId: number, error: Error): void {
    const entry = this.take(requestId);
    if (entry) entry.reject(error);
  }

  /**
   * Fail everything outstanding — worker teardown or re-init. Without it, a
   * disposed worker's callers each wait a full timeout for an answer that can
   * never come.
   */
  clear(reason: string): void {
    const entries: Array<Entry<T>> = [];
    this.pending.forEach((entry) => entries.push(entry));
    this.pending.clear();
    for (let i = 0; i < entries.length; i++) {
      clearTimeout(entries[i].timer);
      entries[i].reject(new Error(reason));
    }
  }

  private take(requestId: number): Entry<T> | null {
    const entry = this.pending.get(requestId);
    if (!entry) return null;
    clearTimeout(entry.timer);
    this.pending.delete(requestId);
    return entry;
  }
}

export const DETECT_TIMEOUT_ERROR = "DetectTimeoutError";

export function isDetectTimeout(error: unknown): boolean {
  return !!error && (error as Error).name === DETECT_TIMEOUT_ERROR;
}
